import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Lock, Mail, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

export default function Vendors() {
  const { user } = useAuth();
  const [vendors, setVendors] = useState([]);
  const [commodities, setCommodities] = useState([]);
  const [filter, setFilter] = useState("all");
  const [introOpen, setIntroOpen] = useState(false);
  const [introBusy, setIntroBusy] = useState(false);
  const [intro, setIntro] = useState({ commodity_code: "WHEAT", region: "", volume_estimate: "", notes: "" });
  const [requests, setRequests] = useState([]);

  const load = async () => {
    const url = filter === "all" ? "/vendors" : `/vendors?commodity=${filter}`;
    const [v, c] = await Promise.all([api.get(url), api.get("/commodities")]);
    setVendors(v.data); setCommodities(c.data);
    if (user?.tier !== "free") {
      try { const { data } = await api.get("/vendors/intro-request"); setRequests(data); } catch {}
    }
  };
  useEffect(() => { load(); }, [filter, user?.tier]); // eslint-disable-line

  const submitIntro = async () => {
    setIntroBusy(true);
    try {
      const { data } = await api.post("/vendors/intro-request", intro);
      toast.success(data.message || "Intro request queued");
      setIntroOpen(false);
      setIntro({ commodity_code: "WHEAT", region: "", volume_estimate: "", notes: "" });
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setIntroBusy(false); }
  };

  const isPro = user?.tier !== "free";

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-primary">Sourcing</div>
          <h1 className="mt-1 font-display text-3xl font-bold tracking-tight">Vendor comparison</h1>
          <p className="mt-1 text-sm text-muted-foreground">Curated global suppliers · basis vs benchmark · negotiated rates for Enterprise.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-56">
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger data-testid="vendors-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All commodities</SelectItem>
                {commodities.map(c => <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Dialog open={introOpen} onOpenChange={setIntroOpen}>
            <DialogTrigger asChild>
              <Button data-testid="request-intro-btn" className="rounded-full" disabled={!isPro} title={isPro ? "" : "Pro tier required"}>
                <Sparkles className="mr-2 h-4 w-4" />Request supplier intro
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="font-display">Request a new supplier</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Commodity</label>
                  <Select value={intro.commodity_code} onValueChange={v => setIntro({...intro, commodity_code: v})}>
                    <SelectTrigger data-testid="intro-commodity"><SelectValue /></SelectTrigger>
                    <SelectContent>{commodities.map(c => <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Preferred region</label>
                  <Input data-testid="intro-region" placeholder="e.g. South America, EU, SEA" value={intro.region} onChange={e => setIntro({...intro, region: e.target.value})} />
                </div>
                <div>
                  <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Volume estimate</label>
                  <Input data-testid="intro-volume" placeholder="e.g. 2,000 MT/qtr" value={intro.volume_estimate} onChange={e => setIntro({...intro, volume_estimate: e.target.value})} />
                </div>
                <div>
                  <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Notes</label>
                  <Textarea data-testid="intro-notes" rows={3} placeholder="Quality specs, certifications, timing…" value={intro.notes} onChange={e => setIntro({...intro, notes: e.target.value})} />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setIntroOpen(false)}>Cancel</Button>
                <Button data-testid="intro-submit" onClick={submitIntro} disabled={introBusy}>{introBusy ? "Sending…" : "Submit request"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {isPro && requests.length > 0 && (
        <Card className="border-border/60 bg-card/60">
          <CardHeader><CardTitle className="text-sm font-mono uppercase tracking-widest text-muted-foreground">Your intro requests</CardTitle></CardHeader>
          <CardContent>
            <div className="divide-y divide-border/60">
              {requests.map(r => (
                <div key={r.id} data-testid={`intro-req-${r.id}`} className="flex items-center justify-between py-2 text-sm">
                  <div>
                    <Badge variant="outline" className="mr-2 uppercase text-[10px]">{r.commodity_code}</Badge>
                    <span className="text-muted-foreground">{r.region || "any region"} · {r.volume_estimate || "volume tbd"}</span>
                  </div>
                  <Badge variant="outline" className="uppercase text-[10px] signal-hold border">{r.status}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="border-border/60 bg-card/60">
        <CardContent className="p-0">
          <div className="term-scroll overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border/60 bg-background/40 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                <tr>
                  <th className="px-4 py-3 text-left">Vendor</th>
                  <th className="px-4 py-3 text-left">Commodity</th>
                  <th className="px-4 py-3 text-right">Basis</th>
                  <th className="px-4 py-3 text-left">Quality</th>
                  <th className="px-4 py-3 text-left">MOQ</th>
                  <th className="px-4 py-3 text-right">Lead</th>
                  <th className="px-4 py-3 text-center">Risk</th>
                  <th className="px-4 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/60">
                {vendors.map(v => (
                  <tr key={v.id} data-testid={`vendor-${v.id}`} className={`${v.locked ? "opacity-70" : "transition hover:bg-secondary/30"}`}>
                    <td className="px-4 py-3">
                      <div className="font-semibold">{v.name}</div>
                      <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{v.country}</div>
                    </td>
                    <td className="px-4 py-3"><Badge variant="outline" className="uppercase text-[10px]">{v.commodity_code}</Badge></td>
                    <td className="px-4 py-3 text-right num">
                      {v.locked
                        ? <Lock className="ml-auto h-3 w-3 text-muted-foreground" />
                        : (
                          <span>
                            {v.basis_price >= 0 ? "+" : ""}{v.basis_price?.toFixed(2)}
                            {v.negotiated_rate != null && (
                              <span className="ml-2 text-[10px] font-mono text-[hsl(var(--signal-buy))]">NEG {v.negotiated_rate >= 0 ? "+" : ""}{v.negotiated_rate.toFixed(2)}</span>
                            )}
                          </span>
                        )
                      }
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{v.quality_grade}</td>
                    <td className="px-4 py-3 text-xs">{v.min_order_qty}</td>
                    <td className="px-4 py-3 text-right num">{v.lead_time_days}d</td>
                    <td className="px-4 py-3 text-center"><Badge variant="outline" className="text-[10px]">{v.risk_rating}</Badge></td>
                    <td className="px-4 py-3 text-right">
                      {v.locked ? (
                        <Link to="/app/pricing"><Button size="sm" variant="outline" data-testid={`vendor-upgrade-${v.id}`} className="rounded-full">Upgrade</Button></Link>
                      ) : (
                        <a href={`mailto:${v.contact}`} data-testid={`vendor-contact-${v.id}`}><Button size="sm" variant="outline" className="rounded-full"><Mail className="mr-1 h-3 w-3" />Contact</Button></a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
